/**
 * ContentMarkers — `.marker-row` panel from the prototype
 * (cms-redesign-mockup.html ~4502–4532 CSS, ~8436–8470 markup).
 *
 *   <ContentMarkers
 *     contentStart={formData.session_content_start_timestamp}
 *     allowRating={!!formData.session_allow_rating}
 *     ratingStart={formData.session_rating_start}
 *     ratingEnd={formData.session_rating_end}
 *     maxSec={formData.session_length_in_sec}
 *     onChange={(name, value) => setField(name, value)}
 *   />
 *
 * Renders the cream-bordered box of four rows from the prototype:
 *   1. Content Start         (time input → session_content_start_timestamp)
 *   2. Allow Intensity Rating (toggle    → session_allow_rating)
 *   3. Rating Start          (time input → session_rating_start)
 *   4. Rating End            (time input → session_rating_end)
 *
 * Rows 3 + 4 dim + disable when `allowRating` is off — the toggle
 * row itself stays interactive so the user can flip it back on
 * without first finding it somewhere else.
 *
 * Inputs are MM:SS strings on the wire — we parse on commit (blur
 * or Enter) and clamp to the audio's total duration when known.
 * The parent receives raw seconds via `onChange(name, sec)` so
 * `setField('session_content_start_timestamp', 30)` Just Works.
 */
import React, { useEffect, useState } from 'react';
import {
  formatTimeToMMSS,
  parseMMSSToSeconds,
} from '../../../utils/timeFormat';

/* ─── MM:SS input that holds a local string but commits in seconds ── */

function MmSsInput({ value, onCommit, disabled, max, ariaLabel }) {
  const [local, setLocal] = useState(formatTimeToMMSS(value || 0));

  // Sync when the canonical seconds change externally (e.g. via
  // marker drag, import, or the audio duration becoming known).
  useEffect(() => {
    setLocal(formatTimeToMMSS(value || 0));
  }, [value]);

  const commit = (raw) => {
    const sec = parseMMSSToSeconds(raw);
    if (Number.isNaN(sec) || sec < 0) {
      // Reset to last good value on invalid input rather than
      // surfacing a banner — the placeholder hint is enough.
      setLocal(formatTimeToMMSS(value || 0));
      return;
    }
    const clamped = max != null ? Math.min(sec, max) : sec;
    onCommit(clamped);
    setLocal(formatTimeToMMSS(clamped));
  };

  return (
    <input
      aria-label={ariaLabel}
      value={local}
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit(e.currentTarget.value);
          e.currentTarget.blur();
        }
      }}
      onFocus={(e) => {
        e.currentTarget.style.background = '#FAF7F1'; /* surface */
        e.currentTarget.style.borderColor = '#75A293'; /* teal-green */
        e.currentTarget.style.boxShadow = '0 0 0 3px #75A29333'; /* teal-soft */
      }}
      onBlurCapture={(e) => {
        e.currentTarget.style.background = '#FAF4EA';
        e.currentTarget.style.borderColor = '#E8E0CF';
        e.currentTarget.style.boxShadow = 'none';
      }}
      placeholder="00:00"
      className="font-mono"
      style={{
        width: 88,
        fontSize: 13.5,
        fontWeight: 500,
        color: '#2C3E50',
        padding: '8px 12px',
        textAlign: 'center',
        background: '#FAF4EA',
        border: '1px solid #E8E0CF',
        borderRadius: 6,
        outline: 'none',
        letterSpacing: '0.04em',
        transition: 'all 160ms ease',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        flexShrink: 0,
      }}
    />
  );
}

/* ─── Compact toggle (matches SettingsCard's row pill) ──────── */

function CompactToggle({ checked, onChange, ariaLabel }) {
  return (
    <label
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        width: 36,
        height: 20,
        flexShrink: 0,
        cursor: 'pointer',
        display: 'inline-block',
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 999,
          background: checked ? '#75A293' : '#E8E0CF',
          transition: 'background 160ms ease',
        }}
      />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 1px 3px #2C3E5040',
          transition: 'left 160ms ease',
        }}
      />
    </label>
  );
}

/* ─── Single row ────────────────────────────────────────────── */

function MarkerRow({ label, desc, isLast, disabled, children }) {
  return (
    <div
      className="flex items-center"
      style={{
        gap: 16,
        padding: '12px 0',
        borderBottom: isLast ? 'none' : '1px solid #ECE3D2', /* line */
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 500,
            color: disabled ? '#9CA1A8' : '#2C3E50',
            transition: 'color 160ms ease',
          }}
        >
          {label}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 11.5,
            color: disabled ? '#9CA1A866' : '#9CA1A8',
            transition: 'color 160ms ease',
          }}
        >
          {desc}
        </div>
      </div>
      {children}
    </div>
  );
}

/* ─── Main ────────────────────────────────────────────────── */

export default function ContentMarkers({
  contentStart,
  allowRating,
  ratingStart,
  ratingEnd,
  maxSec,
  onChange,
}) {
  return (
    <div
      style={{
        background: '#FAF4EA', /* cream */
        border: '1px solid #E8E0CF', /* sand */
        borderRadius: 8,
        padding: '4px 14px',
        minWidth: 0,
      }}
    >
      <MarkerRow
        label="Content Start"
        desc='When the session "real" content begins, after any intro'
      >
        <MmSsInput
          ariaLabel="Content start time"
          value={contentStart}
          max={maxSec}
          onCommit={(sec) => onChange('session_content_start_timestamp', sec)}
        />
      </MarkerRow>

      <MarkerRow
        label="Allow Intensity Rating"
        desc="Prompt users to rate before & after"
      >
        <CompactToggle
          ariaLabel="Allow intensity rating"
          checked={allowRating}
          onChange={(e) => onChange('session_allow_rating', e.target.checked ? 1 : 0)}
        />
      </MarkerRow>

      <MarkerRow
        label="Rating Start"
        desc="When the pre-rating prompt appears"
        disabled={!allowRating}
      >
        <MmSsInput
          ariaLabel="Rating start time"
          value={ratingStart}
          max={maxSec}
          disabled={!allowRating}
          onCommit={(sec) => onChange('session_rating_start', sec)}
        />
      </MarkerRow>

      <MarkerRow
        label="Rating End"
        desc="When the post-rating prompt appears"
        disabled={!allowRating}
        isLast
      >
        <MmSsInput
          ariaLabel="Rating end time"
          value={ratingEnd}
          max={maxSec}
          disabled={!allowRating}
          onCommit={(sec) => onChange('session_rating_end', sec)}
        />
      </MarkerRow>
    </div>
  );
}
